import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../App";

const fmt$ = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function UpsellProposalPrint() {
  const { coId } = useParams();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get(`/change-orders/${coId}`)
      .then((r) => { setCo(r.data); setLoading(false); })
      .catch(() => { setError("Proposal not found."); setLoading(false); });
  }, [coId]);

  if (loading) return <div style={s.loading}>Loading proposal…</div>;
  if (error || !co) return <div style={s.loading}>{error || "Not found."}</div>;

  const total = co.final_client_price || co.total_amount || 0;
  const deposit = co.deposit_amount || total;
  const balance = co.balance_amount || 0;
  const depositPct = total > 0 ? Math.round((deposit / total) * 100) : 50;
  const lineItems = co.line_items || [];
  const title = co.title || co.category?.replace(/_/g, " ") || "Upsell Proposal";
  const project = co.project_name || "—";
  const client = co.client_name || "—";
  const approvalUrl = co.approval_url || (co.public_token ? `${window.location.origin}/approval/${co.public_token}` : null);
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div style={s.page}>
      {/* Print toolbar — hidden on print */}
      <div style={s.toolbar} className="no-print">
        <div style={s.toolbarInner}>
          <span style={{ fontSize: 13, color: "#302E2B", fontWeight: 450 }}>Upsell Proposal — {title}</span>
          <button
            style={s.printBtn}
            onClick={() => window.print()}
          >
            Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Proposal document */}
      <div style={s.doc}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.brand}>The Render Pros</div>
          <div style={s.docLabel}>Upsell Proposal</div>
        </div>

        <h1 style={s.title}>{title}</h1>

        <div style={s.metaRow}>
          <div style={s.metaItem}><span style={s.metaKey}>Project</span><span style={s.metaVal}>{project}</span></div>
          <div style={s.metaItem}><span style={s.metaKey}>Client</span><span style={s.metaVal}>{client}</span></div>
          <div style={s.metaItem}><span style={s.metaKey}>Date</span><span style={s.metaVal}>{today}</span></div>
          {co.sales_rep_name && <div style={s.metaItem}><span style={s.metaKey}>Sales Rep</span><span style={s.metaVal}>{co.sales_rep_name}</span></div>}
        </div>

        <div style={s.divider} />

        {/* Description */}
        {co.description && (
          <section style={s.section}>
            <div style={s.sectionTitle}>Overview</div>
            <p style={s.body}>{co.description}</p>
          </section>
        )}

        {/* Deliverables */}
        {lineItems.length > 0 && (
          <section style={s.section}>
            <div style={s.sectionTitle}>Deliverables</div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...s.th, textAlign: "left" }}>Item</th>
                  <th style={{ ...s.th, textAlign: "center" }}>Qty</th>
                  {total > 0 && <th style={{ ...s.th, textAlign: "right" }}>Unit Price</th>}
                  {total > 0 && <th style={{ ...s.th, textAlign: "right" }}>Total</th>}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 450, fontSize: 13 }}>{li.item_name}</div>
                      {li.description && <div style={{ fontSize: 11, color: "#8A8783", marginTop: 2 }}>{li.description}</div>}
                    </td>
                    <td style={{ ...s.td, textAlign: "center" }}>
                      {li.quantity || 1}{li.unit_type ? ` ${li.unit_type.replace(/_/g, " ")}` : ""}
                    </td>
                    {total > 0 && <td style={{ ...s.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt$(li.unit_price || 0)}</td>}
                    {total > 0 && <td style={{ ...s.td, textAlign: "right", fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>{fmt$(li.total_price || (li.unit_price || 0) * (li.quantity || 1))}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <div style={s.divider} />

        {/* Investment */}
        {total > 0 && (
          <section style={s.section}>
            <div style={s.sectionTitle}>Investment</div>
            <div style={s.investmentCard}>
              <div style={s.investmentRow}>
                <span style={s.investKey}>Total Investment</span>
                <span style={s.investVal}>{fmt$(total)}</span>
              </div>
              <div style={{ ...s.investmentRow, borderTop: "1px solid #F1EFEA", paddingTop: 12, marginTop: 4 }}>
                <div>
                  <span style={s.investKey}>Deposit Due Today ({depositPct}%)</span>
                  <div style={{ fontSize: 11, color: "#8A8783", marginTop: 2 }}>Required to begin production</div>
                </div>
                <span style={{ ...s.investVal, color: "#000000", fontSize: 28 }}>{fmt$(deposit)}</span>
              </div>
              {balance > 0 && (
                <div style={{ ...s.investmentRow, marginTop: 8 }}>
                  <div>
                    <span style={s.investKey}>Balance Due at Delivery ({100 - depositPct}%)</span>
                    <div style={{ fontSize: 11, color: "#8A8783", marginTop: 2 }}>Invoiced upon completion</div>
                  </div>
                  <span style={{ ...s.investVal, color: "#8A8783" }}>{fmt$(balance)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Approval CTA */}
        <section style={s.section}>
          <div style={s.sectionTitle}>Next Step</div>
          <p style={{ ...s.body, marginBottom: 16 }}>
            To approve this upsell and initiate production, please click the link below to submit
            your {depositPct}% deposit. Work begins as soon as payment is confirmed.
          </p>
          {approvalUrl ? (
            <a href={approvalUrl} style={s.cta} target="_blank" rel="noreferrer">
              Approve &amp; Pay Deposit →
            </a>
          ) : (
            <div style={{ ...s.body, color: "#8A8783", fontStyle: "italic" }}>
              Approval link will be provided by your sales representative.
            </div>
          )}
        </section>

        {/* Terms */}
        <div style={s.divider} />
        <div style={s.terms}>
          <strong>Terms:</strong> This proposal is valid for 14 days from the date above.
          The deposit is non-refundable once production begins. The remaining balance is due
          upon delivery of all deliverables. All work remains property of The Render Pros
          until full payment is received.
        </div>

        <div style={s.footer}>
          The Render Pros · therenderpros.com · Confidential
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
        @page { margin: 0.75in; }
      `}</style>
    </div>
  );
}

const s = {
  loading: {
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: "100vh", fontFamily: 'var(--font-sans)',
    color: "#8A8783", fontSize: 14,
  },
  page: {
    minHeight: "100vh", background: "#F4F2EE",
    fontFamily: 'var(--font-sans)',
  },
  toolbar: {
    position: "sticky", top: 0, zIndex: 100,
    background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)",
    borderBottom: "1px solid #F1EFEA",
  },
  toolbarInner: {
    maxWidth: 760, margin: "0 auto", padding: "10px 24px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  printBtn: {
    background: "#000000", color: "#fff", border: "none",
    borderRadius: 8, padding: "8px 18px", fontSize: 13,
    fontWeight: 450, cursor: "pointer", letterSpacing: "-0.1px",
  },
  doc: {
    maxWidth: 720, margin: "32px auto 48px", padding: "48px 52px",
    background: "#fff", border: "1px solid #F1EFEA", borderRadius: 16,
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    marginBottom: 20,
  },
  brand: {
    fontSize: 10, fontWeight: 450, letterSpacing: "1.8px",
    color: "#8A8783", textTransform: "uppercase",
  },
  docLabel: {
    fontSize: 10, fontWeight: 450, letterSpacing: "1.4px",
    color: "#8A8783", textTransform: "uppercase",
  },
  title: {
    fontSize: 32, fontWeight: 300, letterSpacing: "-0.8px",
    color: "#000000", margin: "0 0 20px", lineHeight: 1.15,
  },
  metaRow: {
    display: "flex", flexWrap: "wrap", gap: "12px 28px", marginBottom: 24,
  },
  metaItem: { display: "flex", flexDirection: "column", gap: 2 },
  metaKey: { fontSize: 10, fontWeight: 450, letterSpacing: "1.2px", color: "#8A8783", textTransform: "uppercase" },
  metaVal: { fontSize: 13, fontWeight: 450, color: "#000000" },
  divider: { borderTop: "1px solid #F1EFEA", margin: "24px 0" },
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 9, fontWeight: 450, letterSpacing: "1.6px",
    color: "#8A8783", textTransform: "uppercase", marginBottom: 10,
  },
  body: { fontSize: 14, lineHeight: 1.7, color: "#302E2B", margin: 0 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    fontSize: 9, fontWeight: 450, letterSpacing: "1.2px", color: "#8A8783",
    textTransform: "uppercase", padding: "6px 8px 6px 0",
    borderBottom: "1px solid #F1EFEA",
  },
  td: { padding: "11px 8px 11px 0", borderBottom: "1px solid #F4F2EE", verticalAlign: "top", color: "#302E2B" },
  investmentCard: {
    border: "1px solid #F1EFEA", borderRadius: 12,
    padding: "20px 24px", background: "#FFFFFF",
  },
  investmentRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  investKey: { fontSize: 12, fontWeight: 300, color: "#302E2B" },
  investVal: { fontSize: 20, fontWeight: 300, tabularNums: true, color: "#000000", letterSpacing: "-0.5px" },
  cta: {
    display: "inline-block", padding: "14px 28px",
    background: "#000000", color: "#fff", borderRadius: 10,
    fontSize: 14, fontWeight: 450, textDecoration: "none", letterSpacing: "-0.1px",
  },
  terms: {
    fontSize: 11, lineHeight: 1.65, color: "#8A8783", padding: "0 0 16px",
  },
  footer: {
    marginTop: 32, paddingTop: 16, borderTop: "1px solid #F1EFEA",
    fontSize: 10, color: "#8A8783", textAlign: "center",
    letterSpacing: "0.8px", textTransform: "uppercase",
  },
};
